import React, { useState, useEffect } from 'react';
import { Table, Button, Space, message, Card, Checkbox } from 'antd';
import { PageHeader } from '@ant-design/pro-layout';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useAxios } from "../../hooks/hooks";
import { Link, useNavigate } from "react-router-dom";
import { downloadQRCodePoster, generateQRCodePosterBlob } from "../components/QRCodePoster";
import JSZip from "jszip";
import {
    getReportView,
    getRecommendationsView,
    getBibliography,
    getMaterials,
    getExecutiveChart,
    buildDoc,
    generateReport,
    loadAllPages
} from "./ReportEditorPage";
import { saveAs } from "file-saver";

function QRPage(props) {
    const axios = useAxios();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [projects, setProjects] = useState([]);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [grayscale, setGrayscale] = useState(false);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);

    const getProject = async (id_project) => {
        try{
            const response = await axios.current.get("/project-view/"+id_project);
            return response.data[0];
        }catch(e){
            console.error(e);
            return null;
        }
    }

    const fetchProjects = async (currentPage) => {
        setLoading(true);
        try {
            const response = await axios.current.get("projects", {
                params: {
                    page_size: pageSize,
                    page: currentPage
                }
            });
            setProjects(response.data.rows);
            setTotal(response.data.total_rows);
        } catch (error) {
            console.error("Error fetching projects:", error);
            message.error("Failed to fetch projects");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchProjects(page);
    }, [page, pageSize]);

    const onSelectChange = (newSelectedRowKeys) => {
        setSelectedRowKeys(newSelectedRowKeys);
    };

    const handleDownload = async (id_project) => {
        const project = await getProject(id_project);
        if (project) {
            await downloadQRCodePoster(project, grayscale);
        } else {
            message.error("Project not found");
        }
    }

    const handleBatchRegen = async () => {
        if (selectedRowKeys.length === 0) {
            message.warning("Please select at least one project");
            return;
        }
        const hide = message.loading(`Regenerating ${selectedRowKeys.length} reports...`, 0);
        try {

            for (const id_project of selectedRowKeys) {
                const [project, rep, rec, ex, bibliography, materials] = await Promise.all([
                    getProject(id_project),
                    getReportView(axios, id_project),
                    getRecommendationsView(axios, id_project),
                    getExecutiveChart(axios, id_project),
                    getBibliography(axios),
                    getMaterials(axios)
                ]);

                if (project && rep && rec && bibliography && materials && ex) {
                    const [fullMaterials, fullRep, fullRec, fullBib] = await Promise.all([
                        loadAllPages(axios, materials),
                        loadAllPages(axios, rep),
                        loadAllPages(axios, rec),
                        loadAllPages(axios, bibliography),
                    ]);
                    project.short_name_category = project.short_name_category.toLowerCase();
                    const doc = await buildDoc(axios, project, fullMaterials.rows, fullRep.rows, fullRec.rows, ex, []);

                    let bibOutput = "";
                    fullBib.rows.forEach(function (z) {
                        bibOutput += "@book{" + z.code_bibliography + ",\n";
                        bibOutput += '\tauthor = "{' + z.author_bibliography + '}",\n';
                        bibOutput += '\ttitle = "' + z.title_bibliography + '",\n';
                        bibOutput += '\tyear = "' + z.year_bibliography + '"\n';
                        bibOutput += "}\n\n";
                    });
                    doc.bibliography = bibOutput;

                    // Reconstruct inspected areas
                    let inspected_areas = "";
                    project.buildings.forEach(function (b) {
                        inspected_areas += "\\item " + b.name + " ";
                        b.levels.forEach(function (l) {
                            inspected_areas += "\\begin{itemize} ";
                            inspected_areas += "\\item " + l.name + " ";
                            inspected_areas += "\\end{itemize} ";
                        });
                    });
                    const INSPECTED_AREAS_TEMPLATE = "\\begin{itemize} " + "$$ROWS" + "\\end{itemize} ";
                    doc.inspectedareas = INSPECTED_AREAS_TEMPLATE.replace("$$ROWS", inspected_areas);
                    console.log(doc);

                    await generateReport(axios, project, doc);
                    console.log("Report generated successfully");
                } else {
                    console.error(`Failed to fetch all data for project ${id_project}`);
                }
            }
            message.success(`${selectedRowKeys.length} reports regenerated successfully`);
        } catch (error) {
            console.error("Error regenerating reports:", error);
            message.error("Failed to regenerate reports");
        } finally {
            hide();
        }
    }

    const handleBatchDownload = async () => {
        if (selectedRowKeys.length === 0) {
            message.warning("Please select at least one project");
            return;
        }

        const hide = message.loading(`Generating ZIP file${grayscale ? ' (grayscale)' : ''}...`, 0);
        try {
            const zip = new JSZip();
            for (const id_project of selectedRowKeys) {
                const project = await getProject(id_project);
                const blob = await generateQRCodePosterBlob(project, grayscale);
                zip.file(`QR_Poster_${id_project}${grayscale ? '_Grayscale' : ''}.pdf`, blob);

            }

            const content = await zip.generateAsync({ type: "blob" });
            saveAs(content, `QR_Posters${grayscale ? '_Grayscale' : ''}.zip`);
            message.success("ZIP file downloaded successfully");
        } catch (error) {
            console.error("Error generating ZIP:", error);
            message.error("Failed to generate ZIP file");
        } finally {
            hide();
        }
    };

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id_project',
            key: 'id_project',
            width: 100,
        },
        {
            title: 'Project Name',
            dataIndex: 'place_project',
            key: 'place_project',
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space size="middle">
                    <Button
                        type="primary"
                        onClick={() => navigate(`/dashboard/docs/qr-poster/${record.id_project}`)}
                    >
                        <FontAwesomeIcon icon="eye" className="fa-fw" />&nbsp; View
                    </Button>
                    <Button
                        type="default"
                        onClick={() => handleDownload(record.id_project)}
                    >
                        <FontAwesomeIcon icon="print" className="fa-fw" />&nbsp; Generate
                    </Button>
                    <Button
                        type="default"
                        onClick={() => {
                            setSelectedRowKeys([record.id_project]);
                            handleBatchRegen();
                        }}
                    >
                        <FontAwesomeIcon icon="sync" className="fa-fw" />&nbsp; Regen
                    </Button>
                    <Button
                        type="default"
                        onClick={() => navigate(`/dashboard/docs/clearance-report/${record.id_project}`)}
                    >
                        <FontAwesomeIcon icon="file-circle-check" className="fa-fw" />&nbsp; Clearance
                    </Button>
                </Space>
            ),
        },
    ];

    const rowSelection = {
        selectedRowKeys,
        onChange: onSelectChange,
    };

    return (
        <div style={{ padding: '0px' }}>
            <PageHeader
                title="QR Posters"
                subTitle="Manage and download project QR posters"
                style={{ padding: '0px 0px 16px 0px' }}
                extra={[
                    <Checkbox
                        key="grayscale-toggle"
                        checked={grayscale}
                        onChange={(e) => setGrayscale(e.target.checked)}
                        style={{ marginRight: 16 }}
                    >
                        Grayscale Export
                    </Checkbox>,
                    <Button
                        key="batch-regen"
                        type="default"
                        onClick={handleBatchRegen}
                        style={{ marginRight: 8 }}
                    >
                        <FontAwesomeIcon icon="sync" className="fa-fw" />&nbsp; Batch Regen
                    </Button>,
                    <Button
                        key="batch-download"
                        type="primary"
                        onClick={handleBatchDownload}
                    >
                        <FontAwesomeIcon icon="download" className="fa-fw" />&nbsp; Batch Download
                    </Button>
                ]}
            />
            <Card>
                <Table
                    rowSelection={rowSelection}
                    columns={columns}
                    dataSource={projects}
                    rowKey="id_project"
                    loading={loading}
                    scroll={{ x: 'max-content' }}
                    pagination={{
                        current: page,
                        pageSize: pageSize,
                        total: total,
                        onChange: (p, ps) => {
                            setPage(p);
                            setPageSize(ps);
                        },
                        onShowSizeChange: (p, ps) => {
                            setPage(0);
                            setPageSize(ps);
                        },
                    }}
                />
            </Card>
        </div>
    );
}

export default QRPage;
